/**
 * Cart Tier Progress Bar Component for Focal Theme
 * Displays visual progress bar for multi-tier cart offers
 * Version: 2.0.0 - Optimized for Focal Theme
 * 
 * Usage:
 * <cart-tier-progress-bar>
 *   <div class="tier-progress-container">
 *     <!-- Progress bar HTML structure -->
 *   </div>
 * </cart-tier-progress-bar>
 */

class CartTierProgressBar extends HTMLElement {
  constructor() {
    super();
    
    // State
    this.tierConfig = null;
    this.currentCartTotal = 0;
    this.unlockedTiers = new Set();
    
    // DOM references
    this.container = null;
    this.messageEl = null;
    this.trackEl = null;
    this.fillEl = null;
    this.thresholdsEl = null;
    
    // Bind methods
    this.handleTierUpdate = this.handleTierUpdate.bind(this);
  }

  connectedCallback() {
    console.log('[TierProgressBar] 🎯 Component connected (Focal Theme)');
    
    // Get DOM references
    this.container = this.querySelector('.tier-progress-container');
    this.messageEl = this.querySelector('.tier-message');
    this.trackEl = this.querySelector('.tier-progress-track');
    this.fillEl = this.querySelector('.tier-progress-fill');
    this.thresholdsEl = this.querySelector('.tier-thresholds');
    
    if (!this.container) {
      console.warn('[TierProgressBar] ⚠️ Progress bar container not found');
      return;
    }
    
    // Parse tier config from manager
    this.parseTierConfig();
    
    // Listen to tier updates
    document.addEventListener('tier:updated', this.handleTierUpdate);
    
    // Initial render
    this.renderProgressBar();
    
    // Signal that progress bar is ready (for cart refresh synchronization)
    if (window.CartTierOfferGlobal) {
      window.CartTierOfferGlobal.progressBarReady = true;
      console.log('[TierProgressBar] ✓ Component initialized and ready');
    }
  }

  disconnectedCallback() {
    document.removeEventListener('tier:updated', this.handleTierUpdate);
    console.log('[TierProgressBar] Component disconnected');
  }

  /**
   * Parse tier configuration
   */
  parseTierConfig() {
    const configScript = document.querySelector('cart-tier-offer-manager script[type="application/json"]');
    
    if (!configScript) {
      console.error('[TierProgressBar] ❌ No tier config found');
      return;
    }

    try {
      this.tierConfig = JSON.parse(configScript.textContent);
      console.log('[TierProgressBar] ✅ Config loaded:', this.tierConfig.offers.length, 'tiers');
    } catch (error) {
      console.error('[TierProgressBar] ❌ Error parsing config:', error);
    }
  }

  /**
   * Handle tier update events
   */
  handleTierUpdate(event) {
    console.log('[TierProgressBar] 🔔 Tier updated');
    
    const { cartTotalDollars, unlockedTiers, allTiers } = event.detail;
    
    this.currentCartTotal = cartTotalDollars;
    this.unlockedTiers = new Set(unlockedTiers);
    this.tierConfig = { offers: allTiers };
    
    // Update UI
    this.renderProgressBar();
    this.updateMessages();
    
    // Dispatch completion event after rendering is done
    // Use setTimeout to ensure DOM updates are complete
    setTimeout(() => {
      const completeEvent = new CustomEvent('tier:render:complete', {
        bubbles: true,
        detail: { cartTotal: cartTotalDollars }
      });
      document.dispatchEvent(completeEvent);
      console.log('[TierProgressBar] ✅ Render complete event dispatched');
    }, 100);
  }

  /**
   * Render progress bar structure
   */
  renderProgressBar() {
    if (!this.tierConfig || !this.tierConfig.offers || !this.trackEl) return;
    
    const offers = this.tierConfig.offers;
    const cartTotal = this.currentCartTotal;
    
    // Clear existing thresholds
    if (this.thresholdsEl) {
      this.thresholdsEl.innerHTML = '';
    }
    
    // Calculate progress
    const totalTiers = offers.length;
    const startOffset = 6; // Start percentage
    const endOffset = 94; // End percentage
    
    // Render tier markers
    offers.forEach((offer, index) => {
      this.renderTierMarker(offer, index, totalTiers, startOffset, endOffset);
    });
    
    // Calculate and set fill percentage
    let fillPercent = startOffset;
    
    for (let i = 0; i < offers.length; i++) {
      const tierValue = offers[i].tier_limit;
      const nextTierValue = offers[i + 1] ? offers[i + 1].tier_limit : tierValue;
      
      const currentPct = startOffset + ((i / (totalTiers - 1)) * (endOffset - startOffset));
      const nextPct = startOffset + (((i + 1) / (totalTiers - 1)) * (endOffset - startOffset));
      
      if (cartTotal < tierValue) {
        // Before first tier
        fillPercent = startOffset * 0.5;
        break;
      }
      
      if (cartTotal >= tierValue && cartTotal < nextTierValue) {
        // Between tiers
        const progressBetweenTiers = (cartTotal - tierValue) / (nextTierValue - tierValue);
        fillPercent = currentPct + progressBetweenTiers * (nextPct - currentPct);
        break;
      }
      
      if (cartTotal >= nextTierValue && i === totalTiers - 2) {
        // Past last tier
        fillPercent = endOffset;
      }
    }

    // If cart has reached/exceeded the highest tier, show full completion.
    const maxTierValue = offers[offers.length - 1]?.tier_limit || 0;
    if (cartTotal >= maxTierValue) {
      fillPercent = 100;
    }
    
    // Update fill
    if (this.fillEl) {
      this.fillEl.style.width = `${Math.min(Math.max(fillPercent, 0), 100)}%`;
    }
    
    // Show container
    if (this.container) {
      this.container.style.display = 'block';
    }
  }

  /**
   * Render single tier marker
   */
  renderTierMarker(offer, index, totalCount, startOffset, endOffset) {
    if (!this.thresholdsEl) return;
    
    const isActive = this.currentCartTotal >= offer.tier_limit;
    const propertyName = offer.propery_name || offer.property_name;
    
    // Create marker element
    const marker = document.createElement('div');
    marker.className = `tier-threshold${isActive ? ' active' : ''}`;
    marker.dataset.tier = propertyName;
    
    // Position (evenly spaced)
    const position = startOffset + ((index / (totalCount - 1)) * (endOffset - startOffset));
    marker.style.left = `${Math.min(Math.max(position, 0), 100)}%`;
    
      // Create price label
    const priceLabel = document.createElement('div');
    priceLabel.className = 'tier-price';
    priceLabel.textContent = this.formatMoney(offer.tier_limit * 100);
    marker.appendChild(priceLabel); 
    
    // Create icon wrapper
    const iconWrapper = document.createElement('div');
    iconWrapper.className = 'tier-icon';
    
    if (offer.icon_for_progress_bar) {
      const img = document.createElement('img');
      img.src = offer.icon_for_progress_bar;
      img.alt = offer.offer_text || `Tier ${index + 1}`;
      img.width = 40;
      img.height = 40;
      iconWrapper.appendChild(img);
    } else {
      // Fallback icon
      iconWrapper.innerHTML = `<span class="tier-icon-fallback">🎁</span>`;
    }
    
    marker.appendChild(iconWrapper);
    
  
    
    // Create offer text
    if (offer.offer_text) {
      const offerText = document.createElement('div');
      offerText.className = 'tier-text';
      offerText.textContent = offer.offer_text;
      marker.appendChild(offerText);
    }
    
    this.thresholdsEl.appendChild(marker);
  }

  /**
   * Update messages based on progress
   */
  updateMessages() {
    if (!this.tierConfig || !this.messageEl) return;
    
    const offers = this.tierConfig.offers;
    const cartTotal = this.currentCartTotal;
    
    let message = '';
    
    // Find current position
    const maxThreshold = offers[offers.length - 1].tier_limit;
    
    if (cartTotal >= maxThreshold) {
      // All tiers unlocked
      const lastOffer = offers[offers.length - 1];
      message = lastOffer.post_message || '🎉 All rewards unlocked!';
    } else {
      // Find next tier
      const nextTierIndex = offers.findIndex(o => cartTotal < o.tier_limit);
      
      if (nextTierIndex >= 0) {
        const nextTier = offers[nextTierIndex];
        const remaining = nextTier.tier_limit - cartTotal;
        const remainingCents = Math.round(remaining * 100);
        
        // Build message with pre_message
        message = nextTier.pre_message.replace(
          '[amount]',
          this.formatMoney(remainingCents)
        );
        
        // Add post message from previous tier if exists
        if (nextTierIndex > 0) {
          const prevTier = offers[nextTierIndex - 1];
          if (prevTier.post_message) {
            message = `${prevTier.post_message} | ${message}`;
          }
        }
      }
    }
    
    // Update DOM
    if (this.messageEl) {
      this.messageEl.innerHTML = message;
      this.messageEl.style.display = message ? 'block' : 'none';
    }
  }

  /**
   * Format money (cents to currency)
   */
  formatMoney(cents) {
    // Use Indian Rupee format as per original code
    const rupees = cents / 100;
    return '₹' + rupees.toLocaleString('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  }
}

// Register custom element
if (!customElements.get('cart-tier-progress-bar')) {
  customElements.define('cart-tier-progress-bar', CartTierProgressBar);
  console.log('[TierProgressBar] 🎨 Component registered ✓');
}
